import { memo, useEffect, useMemo, useRef } from 'react';
import { Handle, Position, NodeProps, useReactFlow, useUpdateNodeInternals } from 'reactflow';
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { Globe } from 'lucide-react';
import { SourceNodeData } from '../../types';
import { useUIContext } from '../../providers/UIProvider';
import useStore from '../../store/useStore';
import { calculateHandleStyle, groupHandlesByPosition, NODE_DIMENSIONS } from '../../utils/nodeHelpers';
import { useNodeResizing } from '../../hooks/useNodeResizing';

function JavaScriptSourceNode({
  data,
  selected,
  id,
}: NodeProps<SourceNodeData>) {
  const { isDarkMode } = useUIContext();
  const { updateNode } = useStore();
  const { getEdges } = useReactFlow();
  const clusterLatencies = useStore((state) => state.clusterLatencies);
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeContentRef = useRef<HTMLDivElement>(null);
  
  // Calculate minimum height based on content
  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      // Start with vertical padding to ensure content doesn't touch borders
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      
      // Add header height (measured or estimated)
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;
      
      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;
  
      if (showLabels) {
        let visibleLabelCount = 0;
        if (data.url && data.urlVisibility !== "hidden") visibleLabelCount++;
        if (data.eventVolume && data.eventVolumeVisibility !== "hidden") visibleLabelCount++;
        if (typeof data.latencyImpact === 'number' && data.latencyImpact > 0 && data.latencyImpactVisibility !== "hidden") visibleLabelCount++;
        if (data.eventSpec && data.eventSpecVisibility !== "hidden") visibleLabelCount++;
        if (data.consentTool && data.consentToolVisibility !== "hidden") visibleLabelCount++;
  
        if (visibleLabelCount > 0) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT; // Base for one row
        }
        // Simple heuristic: if more than 2 labels and width is tight, assume a second row for labels
        if (visibleLabelCount > 2 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) { 
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }
  
      const notesExist = data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden";
      if (notesExist) {
        // If notes could be shown, add their minimum required space.
        calculatedHeight += NODE_DIMENSIONS.NOTES_MIN_VISIBLE_HEIGHT_ADDITION;
      }
      
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data]);

  // Use the node resizing hook
  const { 
    onResize, 
    onResizeEnd, 
    currentWidth, 
    currentHeight,
    effectiveMinHeight 
  } = useNodeResizing({
    id,
    nodeType: 'javascript-source', // Pass the node type
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight
  });

  // Effect to update node internals when handles change
  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);
  
  // --- Handle Rendering Logic ---
  const handles = useMemo(() => {
    return Array.isArray(data.handles) ? [...data.handles] : [];
  }, [data.handles]);

  // Group handles by position
  const handlesByPosition = useMemo(() => {
    return groupHandlesByPosition(handles);
  }, [handles]);
  // --- End Handle Rendering Logic ---

  // Find connected cluster ID and update latency impact (existing logic)
  useEffect(() => {
    const edges = getEdges();
    let clusterId = null;
    const nodes = useStore.getState().nodes;
    
    // Helper function to find a cluster connected through a load balancer
    const findClusterThroughLoadBalancer = (nodeId: string, visited = new Set<string>()): string | null => {
      // Prevent infinite loops by tracking visited nodes
      if (visited.has(nodeId)) return null;
      visited.add(nodeId);
      
      // Get all outgoing edges from this node
      const outEdges = edges.filter(edge => edge.source === nodeId);
      
      for (const edge of outEdges) {
        const targetNode = nodes.find(node => node.id === edge.target);
        
        // If target is a cluster, return its ID
        if (targetNode && targetNode.type === 'cluster') {
          return targetNode.id;
        }
        
        // If target is a load balancer, recursively check its connections
        if (targetNode && targetNode.type === 'load-balancer') {
          const clusterIdThroughLoadBalancer = findClusterThroughLoadBalancer(targetNode.id, visited);
          if (clusterIdThroughLoadBalancer) {
            return clusterIdThroughLoadBalancer;
          }
        }
      }
      
      return null;
    };
    
    // Look for edges where this node is the target and the source is a cluster
    const clusterEdges = edges.filter(edge => edge.target === id);
    
    // Find the cluster node among the connected nodes
    for (const edge of clusterEdges) {
      const sourceNode = nodes.find(node => node.id === edge.source);
      if (sourceNode && sourceNode.type === 'cluster') {
        clusterId = sourceNode.id;
        break;
      }
    }
    
    // If no direct connection, look for edges where this node is the source
    if (!clusterId) {
      // First check for direct connections to clusters
      const outgoingEdges = edges.filter(edge => edge.source === id);
      for (const edge of outgoingEdges) {
        const targetNode = nodes.find(node => node.id === edge.target);
        if (targetNode && targetNode.type === 'cluster') {
          clusterId = targetNode.id;
          break;
        }
      }
      
      // If still no cluster found, check for connections through load balancers
      if (!clusterId) {
        clusterId = findClusterThroughLoadBalancer(id);
      }
    }
    
    // If a cluster is connected (directly or through load balancer), update with its latency impact
    if (clusterId) {
      const latencyImpact = clusterLatencies[clusterId];
      
      if (typeof latencyImpact === 'number' && latencyImpact !== data.latencyImpact) {
        updateNode(id, { latencyImpact });
      }
    } 
    // If no cluster is connected, set latency impact to 0
    else if (data.latencyImpact !== 0) {
      updateNode(id, { latencyImpact: 0 });
    }
  }, [id, getEdges, clusterLatencies, data.latencyImpact, updateNode]);

  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6', // blue-500
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}
      
      <div
        className={`
          w-full h-full pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
          flex flex-col justify-center items-center
            ${selected 
              ? "border-blue-500 shadow-blue-100 outline outline-4 outline-blue-500/20 outline-offset-0" 
              : "border-gray-300 dark:border-gray-600"
            }
        `}
      >
        <div ref={nodeContentRef} className="flex items-center justify-center gap-2 node-content">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? 'bg-blue-100 text-blue-600' : 'bg-blue-50 text-blue-600'
            }`}
          >
            <Globe className="h-4 w-4" />
          </div>
          <div className="min-w-0 content-container">
            <h3 className={`font-medium text-sm text-gray-900 dark:text-gray-100 truncate`}>
              {data.label}
            </h3>
            {currentWidth >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD && (
              <div className="labels-container flex flex-wrap gap-x-1">
                {/* URL display - only show if visibility is not hidden */}
                {data.url && data.urlVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-blue-100 text-blue-800"
                  >
                    {(() => {
                      try {
                        if (data.url.startsWith('http://') || data.url.startsWith('https://')) {
                          return new URL(data.url).hostname;
                        } else {
                          return new URL(`https://${data.url}`).hostname;
                        }
                      } catch (e) {
                        return data.url.length > 20 ? data.url.substring(0, 20) + '...' : data.url;
                      }
                    })()}
                  </span>
                )}
                
                {/* Event Volume - only show if visibility is not hidden */}
                {data.eventVolume && data.eventVolumeVisibility !== "hidden" && (
                  <span
                    className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-blue-100 text-blue-800"
                  >
                    {data.eventVolume}
                  </span>
                )}
                
                {/* Latency impact - only show if visibility is not hidden */}
                {typeof data.latencyImpact === 'number' && data.latencyImpact > 0 && data.latencyImpactVisibility !== "hidden" && (
                  <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-yellow-100 text-yellow-800">
                    {data.latencyImpact}ms Faster
                  </span>
                )}
                
                {/* Event Spec - show appropriate label based on selection */}
                {data.eventSpec && data.eventSpecVisibility !== "hidden" && (
                  <span
                    className={`mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate ${
                      data.eventSpec === 'custom' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {data.eventSpec === 'custom' ? 'Custom Spec' : 
                     data.eventSpec === 'analytics-js' ? 'Analytics.js' : 
                     data.eventSpec}
                  </span>
                )}
                
                {/* Consent Tool - only show if visibility is not hidden */}
                {data.consentTool && data.consentToolVisibility !== "hidden" && (
                  <span
                    className={`mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate ${
                      data.consentTool === "other" && data.otherConsentToolName
                        ? "bg-green-100 text-green-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {(() => {
                      if (data.consentTool === "other" && data.otherConsentToolName) {
                        return data.otherConsentToolName;
                      }
                      const CONSENT_TOOLS = [
                        { value: "onetrust", label: "OneTrust" },
                        { value: "cookiebot", label: "CookieBot" },
                        { value: "osano", label: "Osano" },
                        { value: "usercentrics", label: "Usercentrics" },
                        { value: "other", label: "Other" }
                      ];
                      const tool = CONSENT_TOOLS.find(tool => tool.value === data.consentTool);
                      return tool ? tool.label : data.consentTool;
                    })()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        {currentHeight >= NODE_DIMENSIONS.HIDE_NOTES_HEIGHT_THRESHOLD && data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
          <div className="mt-2 text-[8px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded max-h-[80px] overflow-y-auto whitespace-pre-line">
            {data.notes}
          </div>
        )}

        {/* --- Handle Rendering --- */}
        {handles && handles.length > 0 &&
          Object.entries(handlesByPosition).map(([_, positionHandles]) =>
            positionHandles.map((handle, index) => (
              <Handle
                key={`${handle.id}-${handle.color}`}
                id={handle.id}
                type={handle.type}
                position={handle.position as Position}
                className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`}
                style={{
                  ...calculateHandleStyle(index, positionHandles.length, handle.position),
                  backgroundColor: handle.color,
                }}
                // Add isConnectable logic if needed based on handle config
              />
            ))
          )
        }
        {/* --- End Handle Rendering --- */}
      </div>
    </div>
  );
}

export default memo(JavaScriptSourceNode);
